import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  ApiError,
  bookingApi,
  type AvailabilityResponse,
  type AvailabilitySlot,
  type ClubInfo,
  type EquipmentItem,
  type GameKind
} from '@/api/client'
import { FloorMap, type FloorTableStatus } from '@billiard/ui'
import { dateKey, dayLabel, fullDateTime, money, nextDays, timeLabel } from '@/lib/format'
import { Cur } from '@/lib/currency'

const TOTAL_STEPS = 5 // kind, time, equipment, contact, otp (done — отдельный экран)

function durationLabel(min: number): string {
  if (min % 60 === 0) return `${min / 60} ч`
  if (min > 60) return `${Math.floor(min / 60)} ч ${min % 60} мин`
  return `${min} мин`
}

function errMsg(e: unknown): string {
  return e instanceof ApiError ? e.message : 'Что-то пошло не так. Попробуйте ещё раз.'
}

export function BookingPage() {
  const { slug = '' } = useParams()

  const [club, setClub] = useState<ClubInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [fatal, setFatal] = useState<string | null>(null)

  const [step, setStep] = useState(0)
  const [gameKind, setGameKind] = useState<GameKind | null>(null)
  const [date, setDate] = useState<string | null>(null)
  const [avail, setAvail] = useState<AvailabilityResponse | null>(null)
  const [availLoading, setAvailLoading] = useState(false)
  const [slot, setSlot] = useState<AvailabilitySlot | null>(null)
  // Выбор стола: id конкретного стола | 'any' (любой, автоназначение) | null (ещё не выбран).
  const [tableSel, setTableSel] = useState<string | 'any' | null>(null)
  const [durationMin, setDurationMin] = useState(0)

  const [equipment, setEquipment] = useState<EquipmentItem[]>([])
  const [qty, setQty] = useState<Record<string, number>>({})

  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [resendIn, setResendIn] = useState(0)

  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [result, setResult] = useState<Awaited<ReturnType<typeof bookingApi.createBooking>> | null>(
    null
  )

  const tz = club?.timezone ?? null

  // Имя типа игры по коду (из справочника клуба); фолбэк — сам код.
  const kindName = useCallback(
    (kindCode: string | null | undefined): string =>
      (kindCode ? club?.gameTypes.find((g) => g.code === kindCode)?.name : null) ??
      (kindCode || '—'),
    [club]
  )

  // ── Загрузка клуба + оборудования ──
  useEffect(() => {
    let alive = true
    setLoading(true)
    Promise.all([bookingApi.clubInfo(slug), bookingApi.equipment(slug).catch(() => [])])
      .then(([info, eq]) => {
        if (!alive) return
        setClub(info)
        setEquipment(eq)
        setDurationMin(Math.max(info.slotMinutes, info.minMinutes))
        if (info.gameKinds.length === 1) setGameKind(info.gameKinds[0])
      })
      .catch((e) => alive && setFatal(errMsg(e)))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [slug])

  // Другой тип игры или другая дата — набор столов другой, выбранный сбрасываем.
  // От длительности набор столов не зависит, поэтому её смена выбор стола не рушит.
  useEffect(() => {
    setTableSel(null)
  }, [slug, gameKind, date])

  // ── Доступность при выборе типа+даты+длительности ──
  // Длительность в запросе обязательна по смыслу: сервер проверяет столы на весь
  // бронируемый интервал, а не на один шаг сетки. Без неё выдача предлагала слот,
  // который упирался в чужую бронь через час, и коммит отвечал 409 — уже после SMS.
  useEffect(() => {
    if (!gameKind || !date || !durationMin) return
    let alive = true
    setAvailLoading(true)
    setSlot(null) // на новой длительности прежний слот мог перестать быть свободным
    bookingApi
      .availability(slug, date, gameKind, durationMin)
      .then((a) => alive && setAvail(a))
      .catch((e) => alive && setErr(errMsg(e)))
      .finally(() => alive && setAvailLoading(false))
    return () => {
      alive = false
    }
  }, [slug, gameKind, date, durationMin])

  // Если для выбранного типа игры доступен только один стол (например, единственный
  // плейстейшен) — выбираем его сразу, не заставляя клиента тыкать в очевидное.
  useEffect(() => {
    if (!club?.allowTableChoice || !avail || avail.tables.length !== 1) return
    const only = avail.tables[0].id
    setTableSel((cur) => cur ?? only)
  }, [club, avail])

  // ── Таймер повторной отправки кода ──
  useEffect(() => {
    if (resendIn <= 0) return
    const t = setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000)
    return () => clearInterval(t)
  }, [resendIn])

  const durations = useMemo(() => {
    if (!club) return []
    const out: number[] = []
    for (let m = club.minMinutes; m <= club.maxMinutes; m += club.slotMinutes) out.push(m)
    return out
  }, [club])

  const days = useMemo(() => nextDays(14), [])
  const todayKey = useMemo(() => dateKey(new Date(), tz), [tz])
  const tomorrowKey = useMemo(() => dateKey(new Date(Date.now() + 86400000), tz), [tz])

  const equipmentTotal = useMemo(
    () => equipment.reduce((sum, it) => sum + (qty[it.id] ?? 0) * it.price, 0),
    [equipment, qty]
  )

  // ── Миникарта зала: только при разрешённом выборе стола и настроенном плане ──
  const floorMap = club?.allowTableChoice ? club.floorMap : null
  const hasMap = !!floorMap && floorMap.tables.length > 0

  // Столы, у которых на выбранный день не осталось ни одного свободного слота.
  const busyAllDay = useMemo(() => {
    if (!avail) return new Set<string>()
    const free = new Set<string>()
    for (const s of avail.slots) for (const id of s.freeTableIds) free.add(id)
    return new Set(avail.tables.filter((t) => !free.has(t.id)).map((t) => t.id))
  }, [avail])

  // Статусы для карты: стола нет в avail.tables (другой тип игры / снят с онлайн-брони)
  // → inactive; занят весь день → busy; иначе free.
  const mapStatuses = useMemo(() => {
    const st: Record<string, FloorTableStatus> = {}
    if (!floorMap) return st
    const inAvail = new Set((avail?.tables ?? []).map((t) => t.id))
    for (const t of floorMap.tables) {
      st[t.id] = !inAvail.has(t.id) ? 'inactive' : busyAllDay.has(t.id) ? 'busy' : 'free'
    }
    return st
  }, [floorMap, avail, busyAllDay])

  // Столы дня, не размещённые на плане (добавлены после настройки плана) — пилюли под картой.
  const offPlanTables = useMemo(() => {
    if (!hasMap || !avail || !floorMap) return []
    const onMap = new Set(floorMap.tables.map((t) => t.id))
    return avail.tables.filter((t) => !onMap.has(t.id))
  }, [hasMap, floorMap, avail])

  // Выбор стола; уже выбранный слот сбрасываем, если он занят для нового стола.
  const pickTable = useCallback(
    (id: string | 'any'): void => {
      setTableSel(id)
      if (id !== 'any' && slot && !slot.freeTableIds.includes(id)) setSlot(null)
    },
    [slot]
  )

  const endsAt = slot ? slot.startsAt + durationMin * 60000 : 0

  const go = (n: number) => {
    setErr(null)
    setStep(n)
  }

  // Сервер в тестовом режиме: код НЕ отправлен. Показываем это честно — иначе
  // страница уверяет, что SMS ушла, и клуб узнаёт о проблеме от гостей.
  const [smsDryRun, setSmsDryRun] = useState(false)

  // ── Действия ──
  const sendCode = useCallback(async () => {
    setBusy(true)
    setErr(null)
    try {
      const res = await bookingApi.requestOtp(slug, phone)
      setSmsDryRun(!!res.dryRun)
      setCode('')
      setResendIn(60)
      setStep(4)
    } catch (e) {
      setErr(errMsg(e))
    } finally {
      setBusy(false)
    }
  }, [slug, phone])

  const resend = useCallback(async () => {
    if (resendIn > 0) return
    setBusy(true)
    setErr(null)
    try {
      const res = await bookingApi.requestOtp(slug, phone)
      setSmsDryRun(!!res.dryRun)
      setResendIn(60)
    } catch (e) {
      setErr(errMsg(e))
    } finally {
      setBusy(false)
    }
  }, [slug, phone, resendIn])

  const confirm = useCallback(async () => {
    if (!slot) return
    setBusy(true)
    setErr(null)
    try {
      const { token } = await bookingApi.verifyOtp(slug, phone, code)
      const equipmentArr = Object.entries(qty)
        .filter(([, q]) => q > 0)
        .map(([itemId, q]) => ({ itemId, qty: q }))
      const res = await bookingApi.createBooking(slug, {
        token,
        name: name.trim(),
        gameKind: gameKind ?? undefined,
        // 'any' = автоназначение на сервере (как при выключенном выборе стола).
        tableId: club?.allowTableChoice && tableSel && tableSel !== 'any' ? tableSel : undefined,
        startsAt: slot.startsAt,
        endsAt: slot.startsAt + durationMin * 60000,
        equipment: equipmentArr.length ? equipmentArr : undefined
      })
      setResult(res)
      setStep(5)
    } catch (e) {
      setErr(errMsg(e))
    } finally {
      setBusy(false)
    }
  }, [slug, phone, code, qty, name, gameKind, slot, durationMin, club, tableSel])

  // ── Состояния загрузки/ошибки ──
  if (loading) {
    return (
      <div className="page">
        <div className="center">
          <div className="spinner" />
        </div>
      </div>
    )
  }
  if (fatal || !club) {
    return (
      <div className="page">
        <div className="shell">
          <div className="card">
            <div className="card-body">
              <p className="empty">{fatal ?? 'Клуб не найден или бронирование недоступно.'}</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Пустой gameKinds ⟺ у клуба нет бронируемых столов (см. availableGameKinds на
  // бэкенде). Мастер в этом случае всегда упрётся в «нет слотов» — поэтому вместо
  // тупикового флоу показываем понятное сообщение.
  if (club.gameKinds.length === 0) {
    return (
      <div className="page">
        <div className="shell">
          <div className="brand">
            <div className="logo">
              8<b>Ball</b>
            </div>
            <div className="sub">{club.name}</div>
          </div>
          <div className="card">
            <div className="card-body">
              <p className="empty">
                Онлайн-бронирование для этого клуба пока недоступно — столы ещё не настроены.
                Пожалуйста, свяжитесь с клубом напрямую, чтобы забронировать стол.
              </p>
            </div>
          </div>
          <div className="footer-note">Скоро здесь можно будет забронировать стол онлайн</div>
        </div>
      </div>
    )
  }

  const setQ = (id: string, delta: number) =>
    setQty((m) => {
      const next = Math.max(0, (m[id] ?? 0) + delta)
      return { ...m, [id]: next }
    })

  return (
    <div className="page">
      <div className="shell">
        <div className="brand">
          <div className="logo">
            8<b>Ball</b>
          </div>
          <div className="sub">{club.name}</div>
        </div>

        <div className="card">
          {step < TOTAL_STEPS && (
            <div className="stepper">
              {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                <div key={i} className={`dot ${i <= step ? 'on' : ''}`} />
              ))}
            </div>
          )}

          <div className="card-body">
            {err && <div className="error">{err}</div>}

            {/* ─── Шаг 0: тип игры ─── */}
            {step === 0 && (
              <>
                <div className="step-title">Выберите игру</div>
                <div className="step-hint">Тип стола для вашей партии</div>
                <div className="choices">
                  {club.gameTypes.map((g) => (
                    <button
                      key={g.code}
                      className={`choice ${gameKind === g.code ? 'on' : ''}`}
                      onClick={() => setGameKind(g.code)}
                    >
                      <div
                        className={`ball ${g.code}`}
                        style={g.color ? { background: g.color, color: '#fff' } : undefined}
                      >
                        {g.code === 'pool' ? '8' : '●'}
                      </div>
                      <div className="name">{g.name}</div>
                    </button>
                  ))}
                </div>
                <div className="actions">
                  <button className="btn btn-primary" disabled={!gameKind} onClick={() => go(1)}>
                    Далее
                  </button>
                </div>
              </>
            )}

            {/* ─── Шаг 1: день + время + длительность ─── */}
            {step === 1 && (
              <>
                <div className="step-title">Дата и время</div>
                <div className="step-hint">Когда вам удобно прийти?</div>

                <div className="chips">
                  {days.map((d) => {
                    const key = dateKey(d, tz)
                    return (
                      <button
                        key={key}
                        className={`chip ${date === key ? 'on' : ''}`}
                        onClick={() => setDate(key)}
                      >
                        <div className="d">{dayLabel(d, tz, todayKey, tomorrowKey)}</div>
                      </button>
                    )
                  })}
                </div>

                {date && (
                  <>
                    <div className="field-label">Длительность</div>
                    <div className="pills">
                      {durations.map((m) => (
                        <button
                          key={m}
                          className={`pill ${durationMin === m ? 'on' : ''}`}
                          onClick={() => setDurationMin(m)}
                        >
                          {durationLabel(m)}
                        </button>
                      ))}
                    </div>

                    {availLoading ? (
                      <div className="center" style={{ minHeight: 80 }}>
                        <div className="spinner" />
                      </div>
                    ) : avail && avail.slots.length > 0 ? (
                      hasMap && floorMap ? (
                        <>
                          {/* ── Миникарта: сначала стол, затем слоты по нему ── */}
                          <div className="field-label">Выберите стол на плане</div>
                          <div className="floor-map-wrap">
                            <FloorMap
                              layout={floorMap}
                              gameTypes={club.gameTypes}
                              statuses={mapStatuses}
                              selectedId={tableSel !== 'any' ? tableSel : null}
                              onSelect={pickTable}
                              fitToContent
                            />
                          </div>
                          <div className="fm-legend">
                            <span className="fm-legend-item is-free">свободен</span>
                            <span className="fm-legend-item is-busy">занят весь день</span>
                          </div>
                          <div className="pills">
                            <button
                              className={`pill ${tableSel === 'any' ? 'on' : ''}`}
                              onClick={() => pickTable('any')}
                            >
                              Любой стол
                            </button>
                            {offPlanTables.map((t) => (
                              <button
                                key={t.id}
                                disabled={busyAllDay.has(t.id)}
                                className={`pill ${tableSel === t.id ? 'on' : ''}`}
                                onClick={() => pickTable(t.id)}
                              >
                                {t.name}
                              </button>
                            ))}
                          </div>

                          <div className="field-label">Время начала</div>
                          <div className="slots">
                            {avail.slots.map((s) => {
                              const free =
                                tableSel && tableSel !== 'any'
                                  ? s.freeTableIds.includes(tableSel)
                                  : s.freeTableIds.length > 0
                              return (
                                <button
                                  key={s.startsAt}
                                  disabled={!free}
                                  className={`slot ${slot?.startsAt === s.startsAt ? 'on' : ''}`}
                                  onClick={() => setSlot(s)}
                                >
                                  {timeLabel(s.startsAt, tz)}
                                </button>
                              )
                            })}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="field-label">Время начала</div>
                          <div className="slots">
                            {avail.slots.map((s) => {
                              const free = s.freeTableIds.length > 0
                              return (
                                <button
                                  key={s.startsAt}
                                  disabled={!free}
                                  className={`slot ${slot?.startsAt === s.startsAt ? 'on' : ''}`}
                                  onClick={() => {
                                    setSlot(s)
                                    setTableSel(null)
                                  }}
                                >
                                  {timeLabel(s.startsAt, tz)}
                                </button>
                              )
                            })}
                          </div>

                          {/* Выбор конкретного стола — только если владелец включил в БО */}
                          {slot && club.allowTableChoice && (
                            <>
                              <div className="field-label">Стол</div>
                              <div className="pills">
                                {avail.tables
                                  .filter((t) => slot.freeTableIds.includes(t.id))
                                  .map((t) => (
                                    <button
                                      key={t.id}
                                      className={`pill ${tableSel === t.id ? 'on' : ''}`}
                                      onClick={() => setTableSel(t.id)}
                                    >
                                      {t.name}
                                    </button>
                                  ))}
                              </div>
                            </>
                          )}
                        </>
                      )
                    ) : (
                      <div className="empty">На этот день нет свободных слотов</div>
                    )}
                  </>
                )}

                <div className="actions">
                  <button className="btn btn-ghost back" onClick={() => go(0)}>
                    Назад
                  </button>
                  <button
                    className="btn btn-primary"
                    disabled={!slot || (club.allowTableChoice && !tableSel)}
                    onClick={() => go(2)}
                  >
                    Далее
                  </button>
                </div>
              </>
            )}

            {/* ─── Шаг 2: оборудование ─── */}
            {step === 2 && (
              <>
                <div className="step-title">Оборудование</div>
                <div className="step-hint">Можно добавить кий, перчатку и др. (необязательно)</div>
                {equipment.length === 0 ? (
                  <div className="empty">Дополнительное оборудование недоступно</div>
                ) : (
                  <div>
                    {equipment.map((it) => (
                      <div className="eq-row" key={it.id}>
                        <div className="eq-info">
                          <div className="eq-name">{it.name}</div>
                          <div className="eq-price">
                            {it.price > 0 ? (
                              <>
                                {money(it.price)} <Cur display={club?.currencyDisplay} />
                              </>
                            ) : (
                              'бесплатно'
                            )}
                            {it.deposit > 0 ? (
                              <>
                                {' · залог '}
                                {money(it.deposit)} <Cur display={club?.currencyDisplay} />
                              </>
                            ) : (
                              ''
                            )}
                          </div>
                        </div>
                        <div className="stepperq">
                          <button onClick={() => setQ(it.id, -1)} aria-label="минус">
                            −
                          </button>
                          <span className="qv">{qty[it.id] ?? 0}</span>
                          <button onClick={() => setQ(it.id, +1)} aria-label="плюс">
                            +
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="actions">
                  <button className="btn btn-ghost back" onClick={() => go(1)}>
                    Назад
                  </button>
                  <button className="btn btn-primary" onClick={() => go(3)}>
                    Далее
                  </button>
                </div>
              </>
            )}

            {/* ─── Шаг 3: контакты ─── */}
            {step === 3 && (
              <>
                <div className="step-title">Ваши контакты</div>
                <div className="step-hint">Имя и телефон для подтверждения брони</div>
                <div className="input-group">
                  <label>Имя</label>
                  <input
                    className="input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Например, Сергей"
                    autoComplete="name"
                  />
                </div>
                <div className="input-group">
                  <label>Телефон</label>
                  <input
                    className="input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+375 (29) 123-45-67"
                    inputMode="tel"
                    autoComplete="tel"
                  />
                </div>
                <div className="actions">
                  <button className="btn btn-ghost back" onClick={() => go(2)}>
                    Назад
                  </button>
                  <button
                    className="btn btn-primary"
                    disabled={busy || name.trim().length < 1 || phone.replace(/\D/g, '').length < 9}
                    onClick={sendCode}
                  >
                    {busy ? 'Отправляем…' : 'Получить код'}
                  </button>
                </div>
                <div className="consent-note">
                  Нажимая «Получить код», вы соглашаетесь с{' '}
                  <a
                    href="https://8ballpos.by/policy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    политикой обработки персональных данных
                  </a>
                  .
                </div>
              </>
            )}

            {/* ─── Шаг 4: OTP ─── */}
            {step === 4 && (
              <>
                <div className="step-title">Подтверждение</div>
                <div className="step-hint">
                  {smsDryRun ? (
                    <span className="warn">
                      Отправка SMS сейчас отключена на сервере — код не пришёл. Обратитесь в клуб
                      или забронируйте через Telegram.
                    </span>
                  ) : (
                    <>
                      Мы отправили код на номер <b>{phone}</b>
                    </>
                  )}
                </div>
                <input
                  className="input otp-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="••••••"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                />
                <div className="otp-meta">
                  {resendIn > 0 ? (
                    <span>Отправить код повторно через {resendIn} с</span>
                  ) : (
                    <button className="linkbtn" onClick={resend} disabled={busy}>
                      Отправить код повторно
                    </button>
                  )}
                </div>
                <div className="actions">
                  <button className="btn btn-ghost back" onClick={() => go(3)}>
                    Назад
                  </button>
                  <button
                    className="btn btn-primary"
                    disabled={busy || code.length < 4}
                    onClick={confirm}
                  >
                    {busy ? 'Бронируем…' : 'Подтвердить бронь'}
                  </button>
                </div>
              </>
            )}

            {/* ─── Шаг 5: готово ─── */}
            {step === 5 && result && (
              <div className="success">
                <div className="seal">✓</div>
                <h2>Бронь подтверждена!</h2>
                <p>
                  {smsDryRun
                    ? 'Бронь принята. Подтверждение по SMS сейчас не отправляется — уточните детали в клубе.'
                    : 'Мы отправили подтверждение по SMS. Ждём вас в клубе.'}
                </p>
                <div className="summary">
                  <div className="row">
                    <span className="k">Игра</span>
                    <span className="v">{kindName(result.gameKind)}</span>
                  </div>
                  <div className="row">
                    <span className="k">Когда</span>
                    <span className="v">{fullDateTime(result.startsAt, tz)}</span>
                  </div>
                  <div className="row">
                    <span className="k">До</span>
                    <span className="v">{timeLabel(result.endsAt, tz)}</span>
                  </div>
                  <div className="row">
                    <span className="k">На имя</span>
                    <span className="v">{result.customerName}</span>
                  </div>
                  {result.items.length > 0 && (
                    <div className="row">
                      <span className="k">Оборудование</span>
                      <span className="v">
                        {result.items.map((i) => `${i.name ?? '—'}×${i.qty}`).join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {step < TOTAL_STEPS && slot && step >= 1 && (
          <div className="footer-note">
            {gameKind ? kindName(gameKind) : ''} · {timeLabel(slot.startsAt, tz)}–
            {timeLabel(endsAt, tz)}
            {tableSel && tableSel !== 'any'
              ? ` · ${avail?.tables.find((t) => t.id === tableSel)?.name ?? ''}`
              : ''}
            {equipmentTotal > 0 ? (
              <>
                {' · оборуд. '}
                {money(equipmentTotal)} <Cur display={club?.currencyDisplay} />
              </>
            ) : (
              ''
            )}
          </div>
        )}
      </div>
    </div>
  )
}
